import React, { useState, useEffect, useCallback, useLayoutEffect, useRef } from 'react';
import { MapPin } from 'lucide-react';
import useOverlayDismiss from '../../Elements/useOverlayDismiss';
import ConfirmDialog from '../../Elements/ConfirmDialog';
import apiFetch, { safeJson, apiErrorDetail } from '../../../context/api';
import { ubicacionPorDefecto, describirUbicacionMovimiento } from '../../../lib/helpers/locations';

// Subpestañas del Historial. Cada una pide su `tipo` al servidor, con su propio
// límite: con el límite sobre la lista mezclada, un día de muchas ventas vaciaba
// Movimientos.
const HIST_SUBTABS = [['movimientos', 'Movimientos'], ['ventas', 'Ventas']];

// La anotación es secundaria: dos líneas en la lista, y un toque la abre entera.
// «más» va encima del final de la segunda línea (no añade una fila): en la tablet
// toda la anotación es la zona táctil, y un tooltip no existe.
function ConceptoHistorial({ texto, abierta, onToggle }) {
  const ref = useRef(null);
  const [desborda, setDesborda] = useState(false);

  useLayoutEffect(() => {
    const el = ref.current;
    // Abierta ya no tiene clamp: scrollHeight y clientHeight coinciden y la
    // medida diría que cabe. Se conserva `desborda` de cuando estaba plegada.
    if (!el || abierta) return;
    setDesborda(el.scrollHeight > el.clientHeight);
  }, [texto, abierta]);

  if (!texto) return null;
  if (!abierta && !desborda) {
    return (
      <div ref={ref} className="line-clamp-2 min-w-0 break-words text-xs text-[var(--muted-foreground)]">
        {texto}
      </div>
    );
  }
  return (
    <button
      type="button"
      aria-expanded={abierta}
      onClick={onToggle}
      className="relative block w-full min-w-0 text-left text-xs text-[var(--muted-foreground)]"
    >
      {abierta ? (
        <span className="block min-w-0 break-words">
          {texto}{' '}
          <span className="font-medium text-[var(--foreground)]">menos</span>
        </span>
      ) : (
        <>
          <span ref={ref} className="line-clamp-2 min-w-0 break-words pr-7">{texto}</span>
          <span className="absolute bottom-0 right-0 bg-[var(--background)] pl-1 font-medium text-[var(--foreground)]">más</span>
        </>
      )}
    </button>
  );
}

/**
 * Modal "Actualizar Stock" (estilo danimateu) para un producto:
 *  - Entrar stock: suma gramos/unidades (+ coste/gramo opcional + concepto)
 *  - Ajustar stock: fija el stock a un valor absoluto (corrección)
 *  - Historial: Movimientos (entrada/salida/ajuste/traspaso) y Ventas (+ anulaciones)
 * Backend: POST /v1/stock/entry, /v1/stock/adjust-location, GET /v1/stock/history
 */
const StockUpdateModal = ({ product, token, venueID, onClose, onUpdated, puedeCuadrarTotal = false }) => {
  const overlayDismiss = useOverlayDismiss(onClose);
  const [tab, setTab] = useState('entrar');           // entrar | ajustar | historial
  const [stock, setStock] = useState(Number(product?.stock_quantity ?? 0));
  const [qty, setQty] = useState('');
  const [unitCost, setUnitCost] = useState('');
  const [newQty, setNewQty] = useState('');
  const [adjustMode, setAdjustMode] = useState('absoluto');   // absoluto | frasco
  const [grossWeight, setGrossWeight] = useState('');
  const [concept, setConcept] = useState('');
  // Historial por subpestaña: { movimientos: {key, movements}, ventas: {...} }. `key` =
  // rango de fechas con el que se cargó; volver a una subpestaña con el mismo rango no
  // repite la petición. Salir de Historial lo vacía: al volver se recarga, como antes.
  const [histSub, setHistSub] = useState('movimientos');
  const [histData, setHistData] = useState({});
  // Filtro de fechas del historial (petición Kong 16-ago): sin fechas, los
  // últimos 100; con rango, hasta 500 de esa ventana. Común a las dos subpestañas.
  const [histFrom, setHistFrom] = useState('');
  const [histTo, setHistTo] = useState('');
  // Una anotación abierta a la vez. La clave lleva subpestaña y fechas: el
  // movimiento no trae id, y si no, al cambiar de lista la abierta saltaría a otra fila.
  const [notaAbierta, setNotaAbierta] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [okMsg, setOkMsg] = useState('');
  // Ubicaciones (Fase C-UI): stock por ubicación + traspaso
  const [byLocation, setByLocation] = useState([]);
  const [locations, setLocations] = useState([]);
  const [fromLoc, setFromLoc] = useState('');
  const [toLoc, setToLoc] = useState('');
  const [transferQty, setTransferQty] = useState('');
  const [entryLoc, setEntryLoc] = useState('');   // ubicación destino al entrar stock
  const [adjustLoc, setAdjustLoc] = useState('');   // ubicación a ajustar (por peso/valor)
  // Cuadrar total con ubicaciones: confirmación explícita (window.confirm se traga
  // en las tablets del club, así que va por ConfirmDialog como el resto).
  const [confirmandoCuadre, setConfirmandoCuadre] = useState(false);
  // Sacar stock (salida) por ubicación
  const [exitQty, setExitQty] = useState('');
  const [exitLoc, setExitLoc] = useState('');

  const isWeight = product?.unit_type === 'weight';
  const unitLabel = isWeight ? 'g' : 'u';
  const step = isWeight ? '0.001' : '1';
  // Tara por defecto del local (fallback cuando ni ubicación ni producto tienen).
  const [venueDefaultTare, setVenueDefaultTare] = useState(null);
  // Tara editable de ESTA pesada: vacío = usar la resuelta; escrita = se guarda en la ubicación.
  const [taraInput, setTaraInput] = useState('');
  // Ajuste POR UBICACIÓN: tara y cantidad actual de la ubicación seleccionada.
  const adjustLocEntry = byLocation.find((b) => String(b.location_id) === String(adjustLoc));
  const adjustLocName = adjustLocEntry?.location_name
    || locations.find((l) => String(l.id) === String(adjustLoc))?.name || '';
  const resolvedTara = Number(adjustLocEntry?.tare_grams ?? product?.tare_grams ?? venueDefaultTare ?? 0);
  const taraSource = adjustLocEntry?.tare_grams != null ? 'de esta ubicación'
    : product?.tare_grams != null ? 'del producto'
    : venueDefaultTare != null ? 'por defecto del local' : 'sin tara configurada';
  const adjustTara = taraInput !== '' && !isNaN(parseFloat(taraInput)) ? parseFloat(taraInput) : resolvedTara;
  const currentLocQty = Number(adjustLocEntry?.quantity ?? 0);
  const adjustNet = adjustMode === 'frasco'
    ? (grossWeight !== '' ? parseFloat(grossWeight) - adjustTara : null)
    : (newQty !== '' ? parseFloat(newQty) : null);
  const descuadre = (adjustNet != null && !isNaN(adjustNet)) ? adjustNet - currentLocQty : null;

  const authHeaders = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
    ...(venueID && { 'x-venue-id': venueID }),
  };

  const histKey = `${histFrom}|${histTo}`;
  const histActual = histData[histSub];
  const movements = histActual?.key === histKey ? histActual.movements : [];
  // Al cambiar de lista la clave volvería a coincidir y la anotación se reabriría sola.
  useEffect(() => { setNotaAbierta(null); }, [histSub, histFrom, histTo]);

  const loadHistory = useCallback(async (sub, signal) => {
    if (!product?.id || !venueID) return;
    setLoading(true);
    setError('');
    try {
      const rango = histFrom || histTo
        ? `${histFrom ? `&from_date=${histFrom}` : ''}${histTo ? `&to_date=${histTo}` : ''}&limit=500`
        : '&limit=100';
      const res = await apiFetch(
        `/v1/stock/history?product_id=${product.id}&venue_id=${venueID}&tipo=${sub}${rango}`,
        { headers: authHeaders, signal }
      );
      const data = await safeJson(res);
      if (!res.ok) throw new Error(apiErrorDetail(data, 'Error al cargar historial'));
      if (signal?.aborted) return;
      setStock(Number(data.stock_quantity ?? stock));
      setHistData((prev) => ({ ...prev, [sub]: { key: `${histFrom}|${histTo}`, movements: data.movements || [] } }));
    } catch (e) {
      // Una respuesta de otra subpestaña o de otro rango ya no le importa a nadie.
      if (signal?.aborted || e.name === 'AbortError') return;
      setError(e.message);
    } finally {
      if (!signal?.aborted) setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product?.id, venueID, token, histFrom, histTo]);

  useEffect(() => {
    if (tab !== 'historial') {
      setHistData({});
      return undefined;
    }
    if (histData[histSub]?.key === histKey) return undefined;
    const controller = new AbortController();
    loadHistory(histSub, controller.signal);
    return () => controller.abort();
  // `histData` fuera a propósito: guardar el resultado no debe relanzar la carga.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, histSub, histKey, loadHistory]);

  const loadLocations = useCallback(async () => {
    if (!product?.id || !venueID) return;
    setLoading(true);
    setError('');
    try {
      const [blRes, locRes] = await Promise.all([
        apiFetch(`/v1/stock/by-location?product_id=${product.id}&venue_id=${venueID}`, { headers: authHeaders }),
        apiFetch(`/v1/stock/locations?venue_id=${venueID}`, { headers: authHeaders }),
      ]);
      const bl = await safeJson(blRes);
      if (!blRes.ok) throw new Error(apiErrorDetail(bl, 'Error al cargar stock por ubicación'));
      const locData = await safeJson(locRes);
      if (!locRes.ok) throw new Error(apiErrorDetail(locData, 'Error al cargar ubicaciones'));
      setStock(Number(bl.stock_quantity ?? stock));
      setByLocation(bl.by_location || []);
      setVenueDefaultTare(bl.venue_default_tare_grams != null ? Number(bl.venue_default_tare_grams) : null);
      setLocations(locData);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product?.id, venueID, token]);

  useEffect(() => {
    if (tab === 'ubicaciones' || tab === 'ajustar' || tab === 'sacar') loadLocations();
  }, [tab, loadLocations]);

  // Cargar ubicaciones también en "Entrar stock" (para elegir destino).
  useEffect(() => {
    if (tab === 'entrar') loadLocations();
  }, [tab, loadLocations]);

  // Ubicación destino por defecto al entrar: la principal (o la primera).
  useEffect(() => {
    if (entryLoc || locations.length === 0) return;
    setEntryLoc(ubicacionPorDefecto(locations).id);
  }, [locations, entryLoc]);

  // Ubicación por defecto a ajustar: la principal (o la primera).
  useEffect(() => {
    if (adjustLoc || locations.length === 0) return;
    setAdjustLoc(ubicacionPorDefecto(locations).id);
  }, [locations, adjustLoc]);

  // Ubicación origen por defecto al sacar: la principal (o la primera).
  useEffect(() => {
    if (exitLoc || locations.length === 0) return;
    setExitLoc(ubicacionPorDefecto(locations).id);
  }, [locations, exitLoc]);

  const submitTransfer = async () => {
    setError(''); setOkMsg('');
    const q = parseFloat(transferQty);
    if (!fromLoc || !toLoc) { setError('Elige origen y destino'); return; }
    if (fromLoc === toLoc) { setError('Origen y destino no pueden ser iguales'); return; }
    if (!q || q <= 0) { setError('Introduce una cantidad mayor que 0'); return; }
    setLoading(true);
    try {
      const res = await apiFetch('/v1/stock/transfer', {
        method: 'POST', headers: authHeaders,
        body: JSON.stringify({ product_id: product.id, venue_id: venueID, from_location_id: fromLoc, to_location_id: toLoc, quantity: q }),
      });
      const data = await safeJson(res);
      if (!res.ok) throw new Error(apiErrorDetail(data, 'Error en el traspaso'));
      setByLocation(data.by_location || []);
      // El total viene en la misma respuesta: sin esto, `brecha` compara un `stock`
      // viejo contra un desglose recién traído y avisa de un fantasma inexistente.
      setStock(Number(data.stock_quantity ?? stock));
      setTransferQty('');
      setOkMsg('Traspaso realizado');
      onUpdated?.();
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const fmt = (n) => Number(n || 0).toLocaleString('es-ES', { minimumFractionDigits: 0, maximumFractionDigits: 3 });

  // Brecha entre el total del producto y lo que suman sus ubicaciones. `by-location` ya
  // devuelve las dos cifras, así que se calcula aquí sin una llamada extra.
  // Positiva = el total dice tener género que no está en ningún estante (el fantasma
  // del cutover que reportó Kong House el 29-08-2026). Negativa = al revés.
  const sumaUbicaciones = byLocation.reduce((acc, b) => acc + Number(b.quantity || 0), 0);
  const brechaCruda = Number((stock - sumaUbicaciones).toFixed(3));
  // `NaN !== 0` es true y `fmt(NaN)` pinta «0»: sin este filtro un total corrupto
  // sacaba el aviso «Faltan 0u» y mandaba expected_total: null (422 con el error
  // crudo de Pydantic en pantalla).
  // Umbral igual al de `scripts/repair_stock_location_gap.py` (|brecha| > 0.01): por
  // debajo de eso el script declara el producto cuadrado, y la app no puede
  // contradecirle con una alarma permanente sobre datos ya reparados.
  const brecha = Number.isFinite(brechaCruda) && Math.abs(brechaCruda) > 0.01 ? brechaCruda : 0;
  // NADA CONTADO: cuadrar aquí deja el producto a 0. Se mira lo que SUMAN las
  // ubicaciones, no cuántas filas hay — una fila a 0 es exactamente igual de «sin
  // contar» que ninguna fila, y desde la app las dos se ven idénticas. Contar filas
  // era el bloqueante 4 del #560: un producto con total 300 y una sola fila a 0
  // ofrecía el botón normal, y pulsarlo borraba 300 unidades reales.
  //
  // No se esconde el botón: la reparación de los fantasmas del traspaso ES este caso
  // (109 productos de Kong el 30-ago). Lo que cambia es que hay que DECLARARLO, porque
  // la diferencia entre «fantasma» y «género real sin contar» no está en los datos.
  const nadaContado = sumaUbicaciones === 0 && stock !== 0;
  // Las ubicaciones suman MÁS que el total: no es esta herramienta (subir el total
  // resucitaría género ya vendido). Se explica, no se ofrece el botón.
  const sobranEnUbicaciones = brecha < 0;
  // El botón NO se ofrece cuando no hay nada contado. Podría ofrecerse —el backend lo
  // permite declarando `confirmar_vaciado`— pero ahí cuadrar VACÍA el producto, y con
  // 110 brechas pendientes y alguien pulsando en cadena eso es exactamente el
  // bloqueante 4. Esos casos van por el script de reparación, que tiene dry-run y lista
  // completa; la interfaz explica lo que pasa y no invita a pulsar.
  // Cuadrar es del PROPIETARIO: retira del total lo que no está en ningún estante,
  // y en un producto grande eso son miles de unidades de una pulsación. El backend
  // lo exige (owner_privileges); aquí se esconde para no enseñar un botón que
  // siempre daría 403 — el mismo criterio que «Eliminar socio» (revisión 31-08).
  const puedeCuadrar = puedeCuadrarTotal && brecha > 0 && !nadaContado && sumaUbicaciones >= 0;

  const submitReconcile = async () => {
    setError(''); setOkMsg('');
    setLoading(true);
    try {
      const res = await apiFetch('/v1/stock/reconcile-total', {
        method: 'POST', headers: authHeaders,
        body: JSON.stringify({
          product_id: product.id,
          venue_id: venueID,
          // El total que el usuario tiene delante: si la BD ya dice otra cosa (una venta
          // en vuelo), el backend contesta 409 en vez de cuadrar sobre un número viejo.
          expected_total: stock,
          // Siempre false desde la interfaz: el botón no se ofrece cuando cuadrar
          // vaciaría el producto, así que este camino nunca lo necesita. Se manda
          // explícito para que se lea que es una decisión y no un olvido — quien SÍ
          // vacía es el script de reparación, que lo pone a true a conciencia.
          confirmar_vaciado: false,
        }),
      });
      const data = await safeJson(res);
      if (!res.ok) throw new Error(apiErrorDetail(data, 'Error al cuadrar el total'));
      setStock(Number(data.stock_quantity));
      setOkMsg(`Total cuadrado con lo contado: ${fmt(data.stock_quantity)}${unitLabel}`);
      await loadLocations();
      onUpdated?.();
    } catch (e) {
      // El backend contesta «vuelve a abrirlo y comprueba el desglose». Si no
      // recargamos, `stock` sigue con el valor viejo y cada reintento manda el mismo
      // expected_total caducado: 409 en bucle hasta cerrar el modal.
      // El orden importa: `loadLocations` arranca con setError(''), así que el aviso
      // se pone DESPUÉS o se borraría solo.
      await loadLocations();
      setError(e.message);
    }
    finally { setLoading(false); }
  };

  const submitEntry = async () => {
    setError(''); setOkMsg('');
    const q = parseFloat(qty);
    if (!q || q <= 0) { setError('Introduce una cantidad mayor que 0'); return; }
    setLoading(true);
    try {
      const res = await apiFetch('/v1/stock/entry', {
        method: 'POST', headers: authHeaders,
        body: JSON.stringify({
          product_id: product.id, venue_id: venueID,
          quantity: q,
          unit_cost: unitCost !== '' ? parseFloat(unitCost) : null,
          concept: concept || null,
          location_id: entryLoc || null,
        }),
      });
      const data = await safeJson(res);
      if (!res.ok) throw new Error(apiErrorDetail(data, 'Error al entrar stock'));
      setStock(Number(data.stock_quantity));
      setOkMsg(`Entrada registrada. Nuevo stock: ${fmt(data.stock_quantity)}${unitLabel}`);
      setQty(''); setUnitCost(''); setConcept('');
      onUpdated?.();
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  };

  const submitExit = async () => {
    setError(''); setOkMsg('');
    const q = parseFloat(exitQty);
    if (!q || q <= 0) { setError('Introduce una cantidad mayor que 0'); return; }
    if (!exitLoc) { setError('Elige la ubicación de la que sacar'); return; }
    const locEntry = byLocation.find((b) => String(b.location_id) === String(exitLoc));
    const avail = Number(locEntry?.quantity ?? 0);
    if (q > avail) { setError(`Stock insuficiente en la ubicación (disponible: ${fmt(avail)}${unitLabel})`); return; }
    setLoading(true);
    try {
      const res = await apiFetch('/v1/stock/exit', {
        method: 'POST', headers: authHeaders,
        body: JSON.stringify({
          product_id: product.id, venue_id: venueID,
          quantity: q,
          location_id: exitLoc || null,
          concept: concept || null,
        }),
      });
      const data = await safeJson(res);
      if (!res.ok) throw new Error(apiErrorDetail(data, 'Error al sacar stock'));
      setStock(Number(data.stock_quantity));
      setOkMsg(`Salida registrada. Nuevo stock: ${fmt(data.stock_quantity)}${unitLabel}`);
      setExitQty(''); setConcept('');
      await loadLocations();
      onUpdated?.();
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  };

  const submitAdjust = async () => {
    setError(''); setOkMsg('');
    if (!adjustLoc) { setError('Elige una ubicación'); return; }
    let target;
    let conceptToSend = concept || null;
    if (adjustMode === 'frasco' && isWeight) {
      const gross = parseFloat(grossWeight);
      if (isNaN(gross) || gross < 0) { setError('Introduce el peso del frasco en báscula'); return; }
      target = gross - adjustTara;
      if (target < 0) { setError(`El peso del frasco (${fmt(gross)}) es menor que la tara (${fmt(adjustTara)})`); return; }
      if (!conceptToSend) conceptToSend = `Ajuste por peso de frasco en ${adjustLocName} (bruto ${fmt(gross)} − tara ${fmt(adjustTara)})`;
    } else {
      if (newQty === '' || parseFloat(newQty) < 0) { setError('Introduce un stock válido (>= 0)'); return; }
      target = parseFloat(newQty);
    }
    setLoading(true);
    try {
      const res = await apiFetch('/v1/stock/adjust-location', {
        method: 'POST', headers: authHeaders,
        body: JSON.stringify({
          product_id: product.id, venue_id: venueID,
          location_id: adjustLoc,
          new_quantity: target,
          concept: conceptToSend,
          // Tara escrita a mano en el pesado → queda guardada para esta ubicación.
          ...(adjustMode === 'frasco' && isWeight && taraInput !== '' && !isNaN(parseFloat(taraInput))
            ? { tare_grams: parseFloat(taraInput) } : {}),
        }),
      });
      const data = await safeJson(res);
      if (!res.ok) throw new Error(apiErrorDetail(data, 'Error al ajustar stock'));
      setStock(Number(data.stock_quantity));
      const d = Number(data.delta);
      setOkMsg(`Ajustado en ${adjustLocName}.${d ? ` Descuadre ${d >= 0 ? '+' : ''}${fmt(d)}${unitLabel}.` : ''} Stock total: ${fmt(data.stock_quantity)}${unitLabel}`);
      setNewQty(''); setGrossWeight(''); setConcept(''); setTaraInput('');
      await loadLocations();
      onUpdated?.();
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  };

  const typeBadge = (t) => {
    const map = {
      entrada: 'bg-emerald-50 text-emerald-700',
      ajuste: 'bg-amber-50 text-amber-700',
      venta: 'bg-slate-100 text-slate-600',
    };
    return map[t] || 'bg-slate-100 text-slate-600';
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" {...overlayDismiss}>
      <div className="w-full max-w-lg rounded-2xl bg-white shadow-xl">
        {/* Cabecera */}
        <div className="flex items-start justify-between border-b border-[#E5E5E5] p-4">
          <div>
            <h3 className="text-base font-semibold text-[#0A0A0A]">Actualizar stock — {product?.name}</h3>
            <p className="mt-0.5 text-sm text-[#737373]">Stock actual: <span className="font-semibold text-[#0A0A0A]">{fmt(stock)}{unitLabel}</span></p>
          </div>
          <button onClick={onClose} className="rounded p-1 text-[#737373] hover:bg-[#F5F5F5]" aria-label="Cerrar">✕</button>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 border-b border-[#E5E5E5] px-4 pt-2">
          {[['entrar', 'Entrar stock'], ['sacar', 'Sacar stock'], ['ajustar', 'Ajustar'], ['ubicaciones', 'Ubicaciones'], ['historial', 'Historial']].map(([k, label]) => (
            <button key={k} onClick={() => { setTab(k); setError(''); setOkMsg(''); }}
              className={`rounded-t-md px-3 py-2 text-sm font-medium transition-colors ${tab === k ? 'bg-[var(--muted)] text-[#0A0A0A]' : 'text-[#737373] hover:text-[#0A0A0A]'}`}>
              {label}
            </button>
          ))}
        </div>

        <div className="p-4">
          {error && <p className="mb-2 rounded-md bg-red-50 px-3 py-2 text-sm text-[#DC2626]">{error}</p>}
          {okMsg && <p className="mb-2 rounded-md bg-emerald-50 px-3 py-2 text-sm text-emerald-700">{okMsg}</p>}

          {tab === 'entrar' && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div className="wa-form-field">
                  <label className="wa-label">Entrar ({unitLabel})</label>
                  <input type="number" step={step} min="0" value={qty} onChange={(e) => setQty(e.target.value)} className="wa-input" placeholder="0" />
                </div>
                <div className="wa-form-field">
                  <label className="wa-label">Coste (Crd/{unitLabel})</label>
                  <input type="number" step="0.01" min="0" value={unitCost} onChange={(e) => setUnitCost(e.target.value)} className="wa-input" placeholder="opcional" />
                </div>
              </div>
              <div className="wa-form-field">
                <label className="wa-label">Ubicación destino</label>
                <select value={entryLoc} onChange={(e) => setEntryLoc(e.target.value)} className="wa-input">
                  {locations.length === 0 && <option value="">Principal (por defecto)</option>}
                  {locations.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
                </select>
              </div>
              <div className="wa-form-field">
                <label className="wa-label">Concepto</label>
                <input type="text" value={concept} onChange={(e) => setConcept(e.target.value)} className="wa-input" placeholder="opcional (ej: compra proveedor)" />
              </div>
              <button onClick={submitEntry} disabled={loading} className="w-full rounded-md bg-lime-500 py-2.5 text-sm font-semibold text-white hover:bg-lime-600 disabled:opacity-60">
                {loading ? 'Guardando…' : 'Entrar stock'}
              </button>
            </div>
          )}

          {tab === 'sacar' && (
            <div className="space-y-3">
              <div className="wa-form-field">
                <label className="wa-label">Ubicación origen</label>
                <select value={exitLoc} onChange={(e) => setExitLoc(e.target.value)} className="wa-input">
                  {locations.length === 0 && <option value="">Principal (por defecto)</option>}
                  {locations.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
                </select>
                <p className="mt-1 text-xs text-[#737373]">
                  Disponible aquí: <strong>{fmt(Number(byLocation.find((b) => String(b.location_id) === String(exitLoc))?.quantity ?? 0))}{unitLabel}</strong>
                </p>
              </div>
              <div className="wa-form-field">
                <label className="wa-label">Sacar ({unitLabel})</label>
                <input type="number" step={step} min="0" value={exitQty} onChange={(e) => setExitQty(e.target.value)} className="wa-input" placeholder="0" />
              </div>
              <div className="wa-form-field">
                <label className="wa-label">Concepto</label>
                <input type="text" value={concept} onChange={(e) => setConcept(e.target.value)} className="wa-input" placeholder="opcional (ej: merma, uso interno)" />
              </div>
              <button onClick={submitExit} disabled={loading} className="w-full rounded-md bg-[#171717] py-2.5 text-sm font-semibold text-white hover:bg-black disabled:opacity-60">
                {loading ? 'Guardando…' : 'Sacar stock'}
              </button>
            </div>
          )}

          {tab === 'ajustar' && (
            <div className="space-y-3">
              <div className="wa-form-field">
                <label className="wa-label">Ubicación a ajustar</label>
                <select value={adjustLoc} onChange={(e) => setAdjustLoc(e.target.value)} className="wa-input">
                  {locations.length === 0 && <option value="">—</option>}
                  {locations.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
                </select>
                <p className="mt-1 text-xs text-[#737373]">Actual en esta ubicación: <strong>{fmt(currentLocQty)}{unitLabel}</strong></p>
              </div>

              {isWeight && (
                <div className="inline-flex rounded-md border border-[#E5E5E5] p-0.5 text-xs">
                  <button onClick={() => setAdjustMode('absoluto')} className={`rounded px-3 py-1.5 font-medium ${adjustMode === 'absoluto' ? 'bg-[#171717] text-white' : 'text-[#737373]'}`}>Valor absoluto</button>
                  <button onClick={() => setAdjustMode('frasco')} className={`rounded px-3 py-1.5 font-medium ${adjustMode === 'frasco' ? 'bg-[#171717] text-white' : 'text-[#737373]'}`}>Peso del frasco</button>
                </div>
              )}

              {adjustMode === 'frasco' && isWeight ? (
                <div className="wa-form-field">
                  <label className="wa-label">Peso del frasco en báscula (bruto, {unitLabel})</label>
                  <input type="number" step={step} min="0" value={grossWeight} onChange={(e) => setGrossWeight(e.target.value)} className="wa-input" placeholder="0.000" />
                  <label className="wa-label mt-2">Tara del frasco ({unitLabel})</label>
                  <input
                    type="number" step={step} min="0" value={taraInput}
                    onChange={(e) => setTaraInput(e.target.value)}
                    className="wa-input" placeholder={fmt(resolvedTara)}
                  />
                  <p className="mt-1 text-xs text-[#737373]">
                    Se aplica <strong>{fmt(adjustTara)}{unitLabel}</strong> ({taraInput !== '' ? 'tara nueva — se guardará para esta ubicación' : taraSource})
                    {adjustNet != null && <> → neto: <strong>{fmt(adjustNet)}{unitLabel}</strong></>}
                  </p>
                </div>
              ) : (
                <div className="wa-form-field">
                  <label className="wa-label">Nuevo stock en esta ubicación ({unitLabel})</label>
                  <input type="number" step={step} min="0" value={newQty} onChange={(e) => setNewQty(e.target.value)} className="wa-input" placeholder={fmt(currentLocQty)} />
                  <p className="mt-1 text-xs text-[#737373]">Fija la cantidad de esta ubicación a este valor (corrección de inventario).</p>
                </div>
              )}

              {descuadre != null && descuadre !== 0 && (
                <p className={`rounded-md px-3 py-2 text-sm font-semibold ${descuadre < 0 ? 'bg-red-50 text-[#DC2626]' : 'bg-amber-50 text-amber-800'}`}>
                  Se hará un descuadre de {descuadre >= 0 ? '+' : ''}{fmt(descuadre)}{unitLabel} ({fmt(currentLocQty)} → {fmt(adjustNet)}{unitLabel} en {adjustLocName})
                </p>
              )}

              <div className="wa-form-field">
                <label className="wa-label">Concepto</label>
                <input type="text" value={concept} onChange={(e) => setConcept(e.target.value)} className="wa-input" placeholder="opcional (ej: recuento, merma)" />
              </div>
              <button onClick={submitAdjust} disabled={loading} className="w-full rounded-md bg-[#171717] py-2.5 text-sm font-semibold text-white hover:bg-black disabled:opacity-60">
                {loading ? 'Guardando…' : 'Ajustar stock'}
              </button>
            </div>
          )}

          {tab === 'ubicaciones' && (
            <div className="space-y-4">
              {/* Stock por ubicación */}
              <div>
                <p className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-[#737373]">Stock por ubicación</p>
                {loading && byLocation.length === 0 && <p className="py-4 text-center text-sm text-[#737373]">Cargando…</p>}
                {!loading && byLocation.length === 0 && <p className="py-4 text-center text-sm text-[#737373]">Sin stock por ubicación.</p>}
                {byLocation.length > 0 && (
                  <ul className="divide-y divide-[#E5E5E5] rounded-md border border-[#E5E5E5]">
                    {byLocation.map((b) => (
                      <li key={b.location_id} className="flex items-center justify-between px-3 py-2 text-sm">
                        <span className="text-[#0A0A0A]">{b.location_name}</span>
                        <span className="font-semibold text-[#0A0A0A]">{fmt(b.quantity)}{unitLabel}</span>
                      </li>
                    ))}
                  </ul>
                )}
                <p className="mt-1.5 text-xs text-[#737373]">Total: <strong>{fmt(stock)}{unitLabel}</strong></p>

                {/* El total y el desglose no cuadran. Hasta ahora el club veía las dos
                    cifras aquí mismo sin ninguna explicación ni forma de resolverlo:
                    Sacar no puede vaciar una ubicación que ya está a 0 y Ajustar arrastra
                    el total con el mismo delta (reporte de Kong House, 29-08-2026).
                    Los tres casos se explican; solo uno tiene botón. */}
                {(brecha !== 0 || nadaContado) && (
                  <div className="mt-3 rounded-md bg-amber-50 px-3 py-2.5">
                    {nadaContado ? (
                      <>
                        <p className="text-sm font-semibold text-amber-800">
                          No hay nada contado en ninguna ubicación.
                        </p>
                        <p className="mt-1 text-xs text-amber-800">
                          El total dice <strong>{fmt(stock)}{unitLabel}</strong> y los
                          estantes suman cero, así que cuadrar dejaría el producto{' '}
                          <strong>a 0</strong> y se perderían esas{' '}
                          <strong>{fmt(stock)}{unitLabel}</strong>.
                        </p>
                        <p className="mt-1 text-xs text-amber-800">
                          Si ese total es un <strong>fantasma del traspaso</strong>, cuádralo.
                          Si el género existe pero nadie lo ha contado, cuéntalo primero en{' '}
                          <strong>Ajustar</strong> —el total subirá de más, es normal— y
                          vuelve aquí.
                        </p>
                      </>
                    ) : sobranEnUbicaciones ? (
                      <>
                        <p className="text-sm font-semibold text-amber-800">
                          Tus ubicaciones suman {fmt(-brecha)}{unitLabel} más que el total.
                        </p>
                        <p className="mt-1 text-xs text-amber-800">
                          Cuadrar solo retira del total lo que no está en ningún estante,
                          nunca lo añade. Esa diferencia suele ser una venta ya servida a la
                          que no se le descontó la ubicación, y no se arregla desde aquí:
                          bajar la ubicación en <strong>Ajustar</strong> arrastraría el
                          total y lo dejaría en negativo. Avísanos con el nombre del producto.
                        </p>
                      </>
                    ) : (
                      <>
                        <p className="text-sm font-semibold text-amber-800">
                          Sobran {fmt(brecha)}{unitLabel} en el total que no están en ninguna ubicación.
                        </p>
                        <p className="mt-1 text-xs text-amber-800">
                          Lo contado en tus ubicaciones suma <strong>{fmt(sumaUbicaciones)}{unitLabel}</strong>.
                          Cuadrar ajusta el total a esa cifra y <strong>no mueve nada de sitio</strong>.
                        </p>
                      </>
                    )}
                    {puedeCuadrar && (
                      <button
                        onClick={() => setConfirmandoCuadre(true)}
                        disabled={loading}
                        className="mt-2 rounded-md bg-amber-700 px-3 py-1.5 text-sm font-semibold text-white hover:bg-amber-800 disabled:opacity-60"
                      >
                        Cuadrar total con lo contado
                      </button>
                    )}
                  </div>
                )}
              </div>

              {/* Traspaso entre ubicaciones */}
              <div className="rounded-lg border border-[#E5E5E5] p-3 space-y-3">
                <p className="text-sm font-semibold text-[#0A0A0A]">Traspasar entre ubicaciones</p>
                <div className="grid grid-cols-2 gap-3">
                  <div className="wa-form-field">
                    <label className="wa-label">Origen</label>
                    <select value={fromLoc} onChange={(e) => setFromLoc(e.target.value)} className="wa-input">
                      <option value="">—</option>
                      {locations.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
                    </select>
                  </div>
                  <div className="wa-form-field">
                    <label className="wa-label">Destino</label>
                    <select value={toLoc} onChange={(e) => setToLoc(e.target.value)} className="wa-input">
                      <option value="">—</option>
                      {locations.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
                    </select>
                  </div>
                </div>
                <div className="wa-form-field">
                  <label className="wa-label">Cantidad ({unitLabel})</label>
                  <input type="number" step={step} min="0" value={transferQty} onChange={(e) => setTransferQty(e.target.value)} className="wa-input" placeholder="0" />
                </div>
                <button onClick={submitTransfer} disabled={loading} className="w-full rounded-md bg-lime-500 py-2.5 text-sm font-semibold text-white hover:bg-lime-600 disabled:opacity-60">
                  {loading ? 'Traspasando…' : 'Traspasar'}
                </button>
              </div>
            </div>
          )}

          {tab === 'historial' && (
            <div className="max-h-80 overflow-y-auto">
              <div className="mb-3 flex gap-2" role="tablist" aria-label="Tipo de historial">
                {HIST_SUBTABS.map(([k, label]) => (
                  <button
                    key={k}
                    type="button"
                    role="tab"
                    aria-selected={histSub === k}
                    onClick={() => { setHistSub(k); setError(''); }}
                    className={`h-8 rounded-sm border border-solid px-3 text-sm font-medium transition-colors ${
                      histSub === k
                        ? 'border-[var(--primary)] bg-[var(--muted)] text-[var(--foreground)]'
                        : 'border-[var(--border)] text-[var(--muted-foreground)] hover:border-[var(--muted-foreground)]'
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
              <div className="mb-3 flex flex-wrap items-center gap-2">
                <input
                  type="date"
                  aria-label="Historial desde"
                  value={histFrom}
                  onChange={(e) => setHistFrom(e.target.value)}
                  className="rounded-md border border-[#E5E5E5] px-2 py-1.5 text-xs"
                />
                <span className="text-xs text-[#737373]">a</span>
                <input
                  type="date"
                  aria-label="Historial hasta"
                  value={histTo}
                  onChange={(e) => setHistTo(e.target.value)}
                  className="rounded-md border border-[#E5E5E5] px-2 py-1.5 text-xs"
                />
                {(histFrom || histTo) && (
                  <button
                    type="button"
                    onClick={() => { setHistFrom(''); setHistTo(''); }}
                    className="rounded-md border border-[#E5E5E5] px-2 py-1.5 text-xs text-[#737373] hover:bg-[#F7F7F3]"
                  >
                    Quitar filtro
                  </button>
                )}
              </div>
              {!histFrom && !histTo && !loading && movements.length >= 100 && (
                <p className="mb-2 text-xs italic text-[#737373]">
                  {histSub === 'ventas'
                    ? 'Mostrando las últimas 100 ventas — filtra por fechas para ver más antiguas.'
                    : 'Mostrando los últimos 100 movimientos — filtra por fechas para ver más antiguos.'}
                </p>
              )}
              {loading && <p className="py-6 text-center text-sm text-[#737373]">Cargando…</p>}
              {!loading && movements.length === 0 && (
                <p className="py-6 text-center text-sm text-[#737373]">
                  {histSub === 'ventas' ? 'Sin ventas.' : 'Sin movimientos.'}
                </p>
              )}
              {!loading && movements.length > 0 && (
                <ul className="divide-y divide-[#E5E5E5]">
                  {movements.map((m, i) => {
                    const ubicacion = describirUbicacionMovimiento(m);
                    const claveNota = `${histSub}|${histFrom}|${histTo}|${i}`;
                    const fecha = m.created_at ? new Date(m.created_at).toLocaleString('es-ES') : '';
                    return (
                    <li key={i} className="flex items-start justify-between gap-3 py-2 text-sm">
                      <div className="min-w-0">
                        <div className="flex min-w-0 flex-wrap items-baseline gap-x-2">
                          <span className={`inline-flex shrink-0 rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase ${typeBadge(m.movement_type)}`}>{m.movement_type}</span>
                          {(fecha || m.created_by) && (
                            <span className="min-w-0 break-words text-xs text-[var(--muted-foreground)]">
                              {fecha}{fecha && m.created_by ? ' · ' : ''}{m.created_by || ''}
                            </span>
                          )}
                        </div>
                        <ConceptoHistorial
                          texto={m.concept}
                          abierta={notaAbierta === claveNota}
                          onToggle={() => setNotaAbierta((actual) => (actual === claveNota ? null : claveNota))}
                        />
                        {ubicacion && (
                          // Salta de línea en vez de truncar: con «…» se perdía justo el destino.
                          <div className="flex min-w-0 items-start gap-1 text-xs text-[var(--muted-foreground)]">
                            <MapPin size={12} strokeWidth={1.5} aria-hidden className="mt-0.5 flex-shrink-0" />
                            <span className="min-w-0 break-words">{ubicacion}</span>
                          </div>
                        )}
                      </div>
                      <div className="flex-shrink-0 text-right">
                        <div className={`font-semibold ${Number(m.quantity) >= 0 ? 'text-emerald-700' : 'text-[#DC2626]'}`}>
                          {Number(m.quantity) >= 0 ? '+' : ''}{fmt(m.quantity)}{unitLabel}
                        </div>
                        {m.balance_after != null && <div className="text-[10px] text-[#A3A3A3]">→ {fmt(m.balance_after)}{unitLabel}</div>}
                        {m.unit_cost != null && <div className="text-[10px] text-[#A3A3A3]">@ {fmt(m.unit_cost)}/{unitLabel}</div>}
                      </div>
                    </li>
                    );
                  })}
                </ul>
              )}
            </div>
          )}
        </div>
      </div>

      <ConfirmDialog
        active={confirmandoCuadre}
        title="¿Cuadrar el total con lo contado?"
        // Todo en `description`: el hueco `note` de ConfirmDialog se pinta en rojo
        // destructivo, y ahí la frase que tranquiliza leía como una alarma.
        description={
          `El total pasará de ${fmt(stock)}${unitLabel} a ${fmt(sumaUbicaciones)}${unitLabel}. `
          + 'No se toca ninguna ubicación: el género que tienes contado en cada estante se '
          + 'queda exactamente donde está. Queda registrado como un ajuste en el historial.'
        }
        confirmLabel="Cuadrar total"
        busy={loading}
        // Cerrar DESPUÉS de la llamada: al revés, `busy` nunca llegaba a true porque el
        // diálogo ya estaba desmontado, y el doble toque no estaba protegido.
        onConfirm={async () => { await submitReconcile(); setConfirmandoCuadre(false); }}
        onCancel={() => setConfirmandoCuadre(false)}
      />
    </div>
  );
};

export default StockUpdateModal;
